export enum DrayageContainerSizesEnum {
  Size20ft = "Size20ft",
  Size40ft = "Size40ft",
  Size45ft = "Size45ft",
  Size53ft = "Size53ft",
}

export enum DrayageDeliveryTypeEnum {
  InternationalImport = "InternationalImport",
  InternationalExport = "InternationalExport",
  DomesticPickup = "DomesticPickup",
  DomesticDelivery = "DomesticDelivery",
  Other = "Other",
}

export interface DrayageLocationAddress {
  city: string | null;
  state: string | null;
  zipCode: string | null;
  address: string | null;
}

export interface DrayageShippingLocationParams {
  terminalGroupId: number | null;
  terminalId: number | null;
  depotId: number | null;
  locationGroupId: number | null;
  tariffPricingTierId: number | null;
}

export interface DrayageLocation extends DrayageShippingLocationParams {
  locationAddress: DrayageLocationAddress | null;
}

export interface RouteSearchFieldMethods {
  clearValue: () => void;
  setValue: (value: string) => void;
}

export enum CustomerProfileTypeOfBusinessEnum {
  Broker = "Broker",
  Shipper = "Shipper",
  FreightForwarder = "FreightForwarder",
}

export interface CustomerProfile {
  id: number;
  origin?: string;
  createdAt?: Date;
  info?: string | null;
  name: string;
  typeOfBusiness: CustomerProfileTypeOfBusinessEnum;
  mcNumber: string;
  email: string;
  phoneNumber: string;
  streetAddress?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  creditLimit?: number | null;
  creditScore?: string | null;
  scacNumber?: string | null;
  billingEmail?: string | null;
  trackingEmail?: string | null;
  notes?: string | null;
}

export interface DepartmentOfEnergyRegion {
  id: number;
  regionName: string;
  paddIdentifier: string;
  averageDieselFuelPricePerGallon: number;
}

export interface FuelSurcharge {
  id: number;
  name: string;
  departmentOfEnergyRegion: DepartmentOfEnergyRegion;
  minimumPricePerGallon: number;
  baseFuelSurchargePercentage: number;
  priceChangeIncrement: number;
  percentIncreasePerIncrement: number;
  averageDieselFuelPricePerGallon: number;
  computedFuelSurchargePercentage: number;
}

export interface OperationExpense {
  id: number;
  name: string;
  milesPerGallon: number;
  truckMonthlyPayment: number;
  truckMilesPerMonth: number;
  insuranceCostPerMile: number;
  truckCostPerMile: number;
  fuelPricePerGallon: number;
  otherCostsPerMile: number;
}

export interface TariffPricingTierLimited {
  id: number;
  name: string;
  description: string;
  fuelSurchargeProfile: FuelSurcharge;
  operationalExpensesProfile: OperationExpense;
}

export interface Coordinates {
  latitude: number;
  longitude: number;
}

export type RouteCostTollType =
  | "barrier"
  | "ticketSystem1"
  | "ticketSystem2"
  | "ticketSystem3";

export interface TicketSystemLocation {
  locationCoordinates: Coordinates;
  name: string;
  road: string;
  state: string;
  country: string;
}

export interface RouteCostToll {
  type: RouteCostTollType;
  cashCost: number | null;
  name: string | null;
  road: string | null;
  state: string | null;
  country: string | null;
  barrierSystemLocation: Coordinates | null;
  ticketSystemStartLocation: TicketSystemLocation;
  ticketSystemEndLocation: TicketSystemLocation;
}

export interface AccessorialCharge {
  value: number;
  chargeType: string;
  order: number;
  notes?: string;
  templateId: number;
  id: number;
  name: string;
}

export interface DrayageQuote {
  quoteResultId: number;
  companyDriverPay: number;
  ownerOperatorPay: number;
  ownerOperatorNetProfit: number;
  ownerOperatorNetProfitMargin: number;
  truck: number;
  fuel: number;
  insurance: number;
  totalExpense: number;
  netProfit: number;
  netProfitMargin: number;
  quotePrice: number;
  quotePriceIncludingTolls: number;
  roundtripMiles: number;
  pickupLocation: string;
  deliveryLocation: string;
  mapSnapshotUrl: string;
  largeMapSnapshotUrl: string;
  goingRouteTolls: RouteCostToll[];
  returnRouteTolls: RouteCostToll[];
  goingRouteTotalTollCost: number;
  returnRouteTotalTollCost: number;
  totalTollsCost: number | null;
  totalTollsCount: number | null;
  linehaulPrice: number;
  fuelSurchargePrice: number;
  fuelSurchargePercentage: number;
  pickupLocationCoordinates: Coordinates;
  deliveryLocationCoordinates: Coordinates;
  routeCoordinates: number[][];
  accessorialCharges: AccessorialCharge[] | null;
}

export interface QuoteTollsEntry {
  quoteResultId: number;
  totalTollsCost: number;
  goingRouteTolls: RouteCostToll[];
  returnRouteTolls: RouteCostToll[];
}

export interface TerminalGroup {
  id: number;
  name: string;
}

export enum TerminalTypeEnum {
  Port = "Port",
  Rail = "Rail",
}

export interface Terminal extends TerminalGroup {
  isEnabled: boolean;
  terminalType: TerminalTypeEnum;
}

export interface Depot extends TerminalGroup {
  isEnabled: boolean;
}

export interface BaseSearchLocations {
  id: number;
  name: string;
}

export interface DrayageSearchShippingLocations {
  terminalGroups?: TerminalGroup[];
  terminals: Terminal[];
  depots: Depot[];
  pricingTiers: BaseSearchLocations[];
  locationGroups: BaseSearchLocations[];
}

export interface DrayageShippingLocationsProperties extends TerminalGroup {
  groupName: string;
}

export interface LocationParams {
  coords?: Coordinates;
  city?: string;
  state?: string;
  zipCode?: string;
  address?: string;
}

export interface AccessorialProfile {
  description: string;
  additionalAccessorialCharges: string;
  accessorialCharges: AccessorialCharge[];
  id: number;
  name: string;
}

export enum RateChargeTypeEnum {
  Flat = "Flat",
  PerMile = "PerMile",
}

export interface TariffPricingBin {
  from: number;
  to: number;
  rateChargeType: RateChargeTypeEnum;
  linehaulRate: number;
  loadPrice: number;
  companyDriverExpense: number;
  companyDriverExpenseChargeType: RateChargeTypeEnum;
  ownerOperatorExpense: number;
  ownerOperatorExpenseChargeType: RateChargeTypeEnum;
}

export interface TariffPricingTier {
  id: number;
  name: string;
  description: string;
  accessorialProfile: AccessorialProfile;
  fuelSurchargeProfile: FuelSurcharge;
  operationalExpensesProfile: OperationExpense;
  tariffPricingBins: TariffPricingBin[];
  terminals: Terminal[];
  depots: Depot[];
}

export interface BaseCustomerProfileParams {
  name: string;
  info?: string | null;
  typeOfBusiness?: CustomerProfileTypeOfBusinessEnum;
  mcNumber: string;
  email: string;
  phoneNumber: string;
  streetAddress?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  creditLimit?: number | null;
  creditScore?: string | null;
  scacNumber?: string | null;
  billingEmail?: string | null;
  trackingEmail?: string | null;
  notes?: string | null;
}

export type BaseCustomerProfileParamsKeys = keyof BaseCustomerProfileParams;

export interface TollBarriersData {
  price: number | null;
  coordinates: number[];
}

export type PaginationResponseType = {
  requestedPageNumber: number;
  requestedPageSize: number;
  totalRecords: number;
  totalPages: number;
};

export enum SortFilterDirectionsEnum {
  Ascending = "Ascending",
  Descending = "Descending",
}

export interface PaginationFilters {
  pageNumber: number;
  pageSize: number;
  includeTotalRecords: boolean;
}

export interface SortingOptionsFilter {
  sortBy: string;
  sortDirection: SortFilterDirectionsEnum;
}

export interface SortingOptions {
  pagination: PaginationFilters;
  sorting?: SortingOptionsFilter;
}
